import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChartsReferenceLine } from '@mui/x-charts';

import ChartCard from 'components/charts/chart-card';
import ChartEmptyState from 'components/charts/chart-empty-state';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import BenchmarkDeltaCell from 'views/benchmark-analytics/components/benchmark-delta-cell';
import BenchmarkGapBand from 'views/benchmark-analytics/components/benchmark-gap-band';
import TrendLineChart from 'views/benchmark-analytics/components/trend-line-chart';
import { COMPANY_SLOT, MARKET_SLOT } from 'views/benchmark-analytics/constants/benchmark-series';
import { useBenchmarkFormatters } from 'views/benchmark-analytics/hooks/use-benchmark-formatters';
import { deltaSentiment } from 'views/benchmark-analytics/utils/benchmark-math';
import { seriesStyle } from 'views/benchmark-analytics/utils/series-style';
import { DEFAULT_GROWTH_FILTERS, GROWTH_DECIMALS } from '../constants/growth';
import { useGrowthState } from '../context/growth-context';

const GrowthTrendCard: React.FC = () => {
  const intl = useIntl();
  const palette = useChartPalette();
  const { growth, farmSeries, filters, setFilters, hasActiveFilters, market, benchmarkQuery } = useGrowthState();
  const { formatValue, formatDelta, formatPeriodLabel, formatAxisPeriodLabel } = useBenchmarkFormatters();
  const categorical = palette.series.categorical;
  const { periods } = growth;
  // Lives here, not in the chart, so remounting the chart on a grouping change keeps hidden series hidden.
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const toggleSeries = (id: string) =>
    setHiddenIds((current) => (current.includes(id) ? current.filter((hiddenId) => hiddenId !== id) : [...current, id]));
  // The band only reads clearly between company and market. With farm lines on top it gets too busy.
  // Loading and empty farms draw no line, so they don't count.
  const showGapBand =
    !hiddenIds.includes('company') &&
    !hiddenIds.includes('market') &&
    farmSeries.every((farm) => farm.status !== 'ready' || hiddenIds.includes(farm.globalSiteId));

  // Same rule as the band: a delta against a hidden line would read as unexplained.
  const deltaFooter = (dataIndex: number) => {
    const delta = growth.delta[dataIndex];
    if (!showGapBand || delta == null) return null;

    const text = formatDelta(delta, GROWTH_DECIMALS);
    return {
      label: intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.table.delta' }),
      value: <BenchmarkDeltaCell text={text} delta={delta} sentiment={deltaSentiment(delta, GROWTH_DECIMALS)} />,
    };
  };

  const isError = benchmarkQuery.isError;
  const isLoading = !isError && benchmarkQuery.isPending;
  const isEmpty = isError || (!isLoading && periods.length === 0);

  // A dashed line at the first period of each new year.
  const yearMarkers = periods.filter((period, index) => index > 0 && periods[index - 1].slice(0, 4) !== period.slice(0, 4));

  const series = useMemo(() => {
    const noData = intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.chart.noData' });

    return [
      {
        id: 'company',
        label: intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.chart.thisCompany' }),
        data: growth.company,
        ...seriesStyle(COMPANY_SLOT, categorical),
      },
      {
        id: 'market',
        label: intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.chart.marketAvg' }, { market: market.shortCode }),
        data: growth.market,
        ...seriesStyle(MARKET_SLOT, categorical),
      },
      ...farmSeries.map((farm) => ({
        id: farm.globalSiteId,
        label: farm.status === 'noData' ? `${farm.name} ${noData}` : farm.name,
        data: farm.values,
        ...seriesStyle(farm.slot, categorical),
      })),
    ];
  }, [intl, growth, farmSeries, market.shortCode, categorical]);

  const emptyState =
    isError ? (
      <ChartEmptyState
        message={<FormattedMessage id="benchmarkAnalytics.growthPage.chart.error" />}
        onClear={() => benchmarkQuery.refetch()}
        clearLabel={<FormattedMessage id="benchmarkAnalytics.growthPage.chart.retry" />}
      />
    ) : hasActiveFilters ? (
      <ChartEmptyState
        message={<FormattedMessage id="benchmarkAnalytics.growthPage.chart.empty" />}
        onClear={() => setFilters(DEFAULT_GROWTH_FILTERS)}
        clearLabel={<FormattedMessage id="benchmarkAnalytics.growthPage.filters.reset" />}
      />
    ) : (
      <ChartEmptyState message={<FormattedMessage id="benchmarkAnalytics.growthPage.chart.empty" />} />
    );

  return (
    <ChartCard
      title={
        <FormattedMessage
          id="benchmarkAnalytics.growthPage.chart.title"
          values={{
            variant: <FormattedMessage id={`benchmarkAnalytics.growthPage.filters.${filters.variant}`} />,
          }}
        />
      }
      loading={isLoading}
      isEmpty={isEmpty}
      emptyState={emptyState}>
      {/* Remount per grouping: MUI X otherwise reads the new year markers against the previous x axis for one render. */}
      <TrendLineChart
        key={filters.grouping}
        points={periods}
        series={series}
        hiddenIds={hiddenIds}
        onToggleSeries={toggleSeries}
        yAxisLabel={intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.chart.yAxis' })}
        valueFormatter={(value) => formatValue(value, GROWTH_DECIMALS)}
        lineChartProps={{
          slotProps: { tooltip: { footer: deltaFooter } },
          xAxis: [
            {
              data: periods,
              scaleType: 'point',
              label: intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.chart.xAxis' }),
              valueFormatter: (key, context) => {
                if (context.location !== 'tick' || key === periods[0]) return formatPeriodLabel(key, filters.grouping);
                return formatAxisPeriodLabel(key, filters.grouping);
              },
            },
          ],
        }}>
        {showGapBand && (
          <BenchmarkGapBand
            points={periods}
            values={growth.company}
            baseline={growth.market}
            aboveColor={palette.status.green}
            belowColor={palette.status.red}
          />
        )}
        {yearMarkers.map((period) => (
          <ChartsReferenceLine
            key={period}
            x={period}
            label={period.slice(0, 4)}
            labelAlign="start"
            lineStyle={{ strokeDasharray: '5 10' }}
          />
        ))}
      </TrendLineChart>
    </ChartCard>
  );
};

export default GrowthTrendCard;
